import { AnyAction } from 'redux';
import {
  AdressvorschlagPayload,
  CALLER_ID,
  CoordinatesChangedPayload,
  GlobalActions,
  ImmobilieUebernehmenPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  Auslandsrisikoort,
  Immobilie,
  PlzChanged,
  VhvVorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import { VhvActions } from '../vhvActions';
import { IAppState } from '../appState';
import { selectIsAuslaendischeAdresse } from '../../app/shared/selectors/risikoort.selector';

function immobilieUebernehmen(
  sectionState: Auslandsrisikoort,
  action: AnyAction,
  appState: IAppState,
): Auslandsrisikoort {
  const actionPayload: ImmobilieUebernehmenPayload = action.payload;
  const newState = {
    ...sectionState,
    isImmobilieImAusland:
      actionPayload.appId === App.VHV_V2 && actionPayload.angebotsId === appState.angebotsId
        ? actionPayload?.isAuslandsImmobilie
        : sectionState.isImmobilieImAusland,
  };

  const immobilie: Immobilie = action.payload.immobilie as Immobilie;
  if (
    !immobilie ||
    actionPayload.appId !== App.VHV_V2 ||
    actionPayload.angebotsId !== appState.angebotsId
  ) {
    return newState;
  }

  const risikoort: Auslandsrisikoort = immobilie.risikoort;
  return {
    ...newState,
    plz: risikoort.plz,
    strasse: risikoort.strasse,
    ort: risikoort.ort,
    hausnummer: risikoort.hausnummer,
    auslandsrisiko: newState?.auslandsrisiko || risikoort?.auslandsrisiko,
    auslandsadresse: risikoort?.auslandsadresse,
  };
}

function cleanAddressForAuslandstarifierung(
  sectionState: Auslandsrisikoort,
  appState: IAppState,
): Auslandsrisikoort {
  return {
    ...sectionState,
    plz: '',
    ort: '',
    strasse: '',
    longtitude: selectIsAuslaendischeAdresse(appState) ? sectionState.longtitude : undefined,
    latitude: selectIsAuslaendischeAdresse(appState) ? sectionState.latitude : undefined,
  };
}

export const risikoortReducer = function (
  sectionState: Auslandsrisikoort = null,
  action: AnyAction,
  appState: IAppState,
): Auslandsrisikoort {
  switch (action.type) {
    case GlobalActions.IMMOBILIE_UEBERNEHMEN:
      return immobilieUebernehmen(sectionState, action, appState);
    case VhvActions.RESET_ADRESSDATEN: {
      return {
        ...sectionState,
        plz: '',
        ort: '',
        strasse: '',
        hausnummer: '',
        latitude: null,
        longtitude: null,
        land: {
          text: '',
          wert: '',
        },
      };
    }
    case VhvActions.PLZ_CHANGED: {
      const actionPayload: PlzChanged = action.payload;
      const geoCoordindatesSelected =
        sectionState.latitude != null && sectionState.longtitude != null;
      return {
        ...sectionState,
        plz: actionPayload.plz,
        ort: '',
        strasse: geoCoordindatesSelected ? sectionState.strasse : '',
        hausnummer: geoCoordindatesSelected ? sectionState.hausnummer : '',
        noneValidated: geoCoordindatesSelected,
      };
    }
    case GlobalActions.ADDRESSVORSCHLAG_SUCCESSFUL: {
      const payload: AdressvorschlagPayload = action.payload;
      const angebotsId = appState.angebotsId;
      const callerId = `${CALLER_ID.VHV_BASISDATEN}_${angebotsId}`;
      if (payload.callerId !== callerId) {
        return sectionState;
      }
      if (payload.orte?.length === 1) {
        return {
          ...sectionState,
          ort: payload.orte[0],
        };
      }
      return sectionState;
    }
    case VhvActions.ORT_CHANGED: {
      return {
        ...sectionState,
        ort: action.payload.ort,
      };
    }
    case VhvActions.STRASSE_CHANGED: {
      return {
        ...sectionState,
        strasse: action.payload.strasse,
      };
    }
    case VhvActions.HAUSNUMMER_CHANGED: {
      return {
        ...sectionState,
        hausnummer: action.payload.hausnummer,
      };
    }
    case VhvActions.LAND_CHANGED: {
      return {
        ...sectionState,
        land: action.payload,
      };
    }
    case VhvActions.MAP_COORDINATES_VHV_CHANGED_EINGABE:
    case VhvActions.MAP_COORDINATES_VHV_CHANGED_ANGEBOT: {
      const actionPayload: CoordinatesChangedPayload = action.payload;
      if (actionPayload?.appId === App.VHV_V2 && actionPayload?.angebotsId === appState.angebotsId) {
        return {
          ...sectionState,
          latitude: actionPayload.coordinates.lat,
          longtitude: actionPayload.coordinates.lon,
          noneValidated: true,
        };
      } else {
        return sectionState;
      }
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED: {
      const actionPayload: VhvVorbelegungdaten = action.payload;
      if (actionPayload.forApp === App.VHV_V2 && actionPayload.angebotsId === appState.angebotsId) {
        return {
          ...sectionState,
          plz: actionPayload.risikoort?.plz,
          strasse: actionPayload.risikoort?.strasse,
          hausnummer: actionPayload.risikoort?.hausnummer,
          ort: actionPayload.risikoort?.ort,
        };
      }
      return sectionState;
    }
    case VhvActions.AUSLAENDISCHE_ADRESSE_CHANGED: {
      return {
        ...sectionState,
        isImmobilieImAusland: action.payload,
      };
    }
    case VhvActions.AUSLANDSADRESSE_CHANGED: {
      return {
        ...sectionState,
        auslandsadresse: action.payload,
      };
    }
    case VhvActions.AUSLANDSRISIKO_CHANGED: {
      return {
        ...sectionState,
        auslandsrisiko: action.payload,
      };
    }
    case VhvActions.CLEAN_ADDRESS_FOR_AUSLANDSTARIFIERUNG:
      return cleanAddressForAuslandstarifierung(sectionState, appState);
    default:
      return sectionState;
  }
};
